import React, {useState} from "react";
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from "../ui/dialog";
import {Label} from "../ui/label";
import {Button} from "../ui/button";
import {Textarea} from "../ui/textarea";
import {alertReplacer, alertReviver, dashboardJsonReviver} from "../Dashboarding/Dashboard";

interface ImportAlertDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onImport: (parsedJson: any) => void;
}
export function ImportAlertDialog({ open, onOpenChange, onImport }: ImportAlertDialogProps) {
    const [jsonInput, setJsonInput] = useState<string>("");
    const [jsonError, setJsonError] = useState<string | null>(null);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const [lineCount, setLineCount] = useState<number>(1);
    const lineNumbersRef = React.useRef<HTMLDivElement>(null);

    const handleJsonInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        setJsonInput(newValue);
        updateLineCount(newValue);

        if (newValue.trim() === '') {
            setJsonError(null);
            return;
        }

        try {
            JSON.parse(newValue);
            setJsonError(null);
        } catch (e) {
            if (e instanceof Error) {
                setJsonError(e.message);
            } else {
                setJsonError("Invalid JSON format");
            }
        }
    };

    const formatJson = () => {
        if (!jsonInput.trim()) return;
        try {
            const formatted = JSON.stringify(JSON.parse(jsonInput), null, 2);
            setJsonInput(formatted);
            updateLineCount(formatted);
            setJsonError(null);
        } catch (e) {
            if (e instanceof Error) {
                setJsonError(e.message);
            } else {
                setJsonError("Invalid JSON format");
            }
        }
    };

    const handleImportJson = () => {
        try {
            const parsedJson = JSON.parse(jsonInput, alertReviver);
            onImport(parsedJson);
            setJsonInput("");
            setJsonError(null);
        } catch (e) {
            setJsonError("Failed to import alert: " + (e instanceof Error ? e.message : "Invalid JSON"));
        }
    };

    const updateLineCount = (text: string) => {
        const lines = text.split('\n').length;
        setLineCount(Math.max(lines, 1));
    };

    const handleScroll = () => {
        if (lineNumbersRef.current && textareaRef.current) {
            lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-normal text-textlight">Import Alert</DialogTitle>
                    <DialogDescription className="text-textmedium">
                        Paste your alert JSON configuration below.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4">
                    <div className="flex justify-between items-center">
                        <Label className={"text-textdark"}>Alert JSON</Label>
                        <Button
                            onClick={formatJson}
                            disabled={!jsonInput.trim()}
                            className="bg-primarytransparent border border-primary text-textmedium hover:text-textlight rounded px-4 h-9"
                        >
                            Format JSON
                        </Button>
                    </div>
                    <div className="flex flex-col gap-2">
                        <div className="flex relative h-[300px]">
                            <div
                                ref={lineNumbersRef}
                                className="line-numbers font-mono text-sm text-textdark bg-backgroundmedium border border-r-0 border-border rounded-l overflow-y-hidden"
                                style={{
                                    padding: "16px",
                                    lineHeight: "21px",
                                    height: "100%",
                                    minWidth: "40px"
                                }}
                            >
                                {Array.from({length: lineCount}, (_, i) => (
                                    <div key={i + 1} className="select-none h-[21px]">
                                        {i + 1}
                                    </div>
                                ))}
                            </div>
                            <Textarea
                                ref={textareaRef}
                                datatype={"json"}
                                placeholder="Paste your alert JSON here"
                                value={jsonInput}
                                onChange={handleJsonInputChange}
                                onScroll={handleScroll}
                                className={"json-textarea border border-border text-textmedium font-mono rounded flex-1 overflow-y-auto resize-none"}
                                style={{
                                    padding: "16px",
                                    lineHeight: "21px",
                                    fontSize: "14px",
                                    height: "100%"
                                }}
                            />
                        </div>
                        {jsonError && (
                            <div className="text-red-500 text-sm mt-2">{jsonError}</div>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        onClick={handleImportJson}
                        disabled={!!jsonError || !jsonInput.trim()}
                        className="bg-primarytransparent border border-primary text-textmedium hover:text-textlight rounded px-4 h-9"
                    >
                        Import JSON
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}